/**
 * Generador de facturas, cotizaciones y vouchers.
 * Formato compacto carta/A4 con 2 copias por hoja (Original/Copia)
 * separadas por línea de corte — al cortar por la mitad cada copia
 * queda completa.
 *
 * Exports:
 *   - generateFacturaHTML     (venta, 2 copias)
 *   - generateCotizacionHTML  (cotización, 2 copias)
 *   - generateOrdenBodegaHTML (orden de bodega, 1 hoja compacta)
 *   - generateTicketHTML      (térmico 80mm, sin cambios)
 *   - generateVoucherHTML     (recibo de pago a empleado, sin cambios)
 */

const formatCurrency = (value) => {
  const num = parseFloat(value || 0);
  return `L. ${isNaN(num) ? '0.00' : num.toFixed(2)}`;
};

const formatFechaHora = (fecha) => {
  const d = fecha ? new Date(fecha) : new Date();
  return {
    fecha: d.toLocaleDateString('es-HN', { day: '2-digit', month: '2-digit', year: 'numeric' }),
    hora: d.toLocaleTimeString('es-HN', { hour: '2-digit', minute: '2-digit', hour12: true }),
  };
};

const formatFechaCorta = (fecha) => {
  if (!fecha) return '';
  const d = new Date(fecha);
  if (isNaN(d.getTime())) return String(fecha);
  return d.toLocaleDateString('es-HN', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const esc = (s) => String(s ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;');

/**
 * Resuelve cantidad y precio de un item venta_detalles para display en la presentación
 * (Caja, Docena, etc.). Maneja dos formatos:
 *  - Post-fix (correcto): cantidad y precio_unitario están en UNIDADES BASE.
 *    cantidad_display = cantidad / factor; precio_display = precio_unitario × factor.
 *  - Legacy (ventas viejas): cantidad y precio_unitario ya están en PRESENTACIÓN.
 *    cantidad_display = cantidad; precio_display = precio_unitario.
 *
 * Heurística: si factor > 1 y cantidad % factor === 0 y cantidad >= factor,
 * asumir post-fix (base). Cualquier otro caso → legacy (no convertir).
 */
export const resolveItemPresentacion = (item) => {
  const cantidadDB = parseFloat(item.cantidad) || 0;
  const precioDB = parseFloat(item.precio_unitario) || 0;
  const factor = parseFloat(item.factor_conversion) > 0 ? parseFloat(item.factor_conversion) : 1;
  const unidad = item.unidad_nombre || item.unidad?.nombre || 'Unidad';
  const tienePresentacion = factor > 1;

  if (!tienePresentacion) {
    return { cantidadDisplay: cantidadDB, precioDisplay: precioDB, equivBase: cantidadDB, precioBase: precioDB, factor: 1, unidad, tienePresentacion: false };
  }

  // Post-fix: cantidad es múltiplo entero del factor y mayor/igual a él.
  const esBase = cantidadDB >= factor && Number.isInteger(cantidadDB / factor);
  if (esBase) {
    return {
      cantidadDisplay: cantidadDB / factor,
      precioDisplay: precioDB * factor,
      equivBase: cantidadDB,
      precioBase: precioDB,
      factor,
      unidad,
      tienePresentacion: true,
    };
  }

  // Legacy: valores ya en presentación.
  return {
    cantidadDisplay: cantidadDB,
    precioDisplay: precioDB,
    equivBase: cantidadDB * factor,
    precioBase: precioDB / factor,
    factor,
    unidad,
    tienePresentacion: true,
  };
};

/* =========================================================================
 *  CSS compartido para factura / cotización (carta portrait, 2 copias)
 * ========================================================================= */
const docStyles = (accent = '#007BFF', accentDark = '#0056b3') => `
  @page { size: letter portrait; margin: 0; }
  * { margin:0; padding:0; box-sizing:border-box; }
  html, body { width:100%; }
  body { font-family: Arial, Helvetica, sans-serif; font-size:10px; line-height:1.2; color:#000; background:#fff; padding: 0.4in 0.4in; }

  .page-container { width:100%; max-width:8in; margin:0 auto; display:flex; flex-direction:column; }

  .cut-line { border:none; border-top:1px dashed #888; margin:6px 0; position:relative; height:10px; }
  .cut-line::before { content:"\\2702"; position:absolute; left:10px; top:-9px; font-size:13px; color:#888; background:#fff; padding:0 4px; }

  .copy-section { padding:6px 10px 8px; border:1px solid #d0d4d8; border-radius:4px; page-break-inside:avoid; flex:1; display:flex; flex-direction:column; min-height:4.6in; }

  /* Header */
  .doc-header { display:flex; justify-content:space-between; align-items:flex-start; gap:8px; border-bottom:2px solid ${accent}; background:linear-gradient(to bottom, #EAF2FE, #fff); margin:-6px -10px 4px -10px; padding:5px 10px; }
  .header-left { display:flex; gap:6px; align-items:flex-start; flex:1; min-width:0; }
  .logo-img { max-height:40px; max-width:60px; object-fit:contain; }
  .company-info { font-size:9px; line-height:1.25; min-width:0; }
  .company-name { font-size:12px; font-weight:bold; color:${accent}; text-transform:uppercase; line-height:1.15; }
  .company-detail { font-size:8.5px; color:#333; }
  .header-center { text-align:center; flex:0 0 auto; padding:0 4px; align-self:center; }
  .doc-title { font-size:14px; font-weight:bold; color:${accent}; text-transform:uppercase; border:2px solid ${accent}; padding:3px 14px; background:#fff; letter-spacing:2px; }
  .header-right { text-align:right; flex:0 0 auto; min-width:130px; font-size:9px; }
  .doc-numero { font-size:12px; font-weight:bold; color:#CC0000; }
  .doc-fecha { font-size:9px; margin-top:1px; }
  .tipo-venta-box { display:flex; gap:8px; margin-top:3px; font-size:9px; justify-content:flex-end; }
  .tipo-venta-box label { display:flex; align-items:center; gap:3px; }
  .cbx { width:11px; height:11px; border:1px solid #000; display:inline-block; text-align:center; line-height:11px; font-size:9px; font-weight:bold; }
  .cbx.checked { background:${accent}; color:#fff; border-color:${accent}; }

  /* Cliente rows */
  .cliente-row { display:flex; align-items:flex-end; margin-bottom:2px; font-size:10px; flex-wrap:nowrap; }
  .cliente-row .lbl { font-weight:bold; min-width:62px; flex:0 0 auto; }
  .cliente-row .val { flex:1 1 0; border-bottom:1px dotted #999; padding:0 4px; min-width:0; }
  .cliente-row .lbl.mid { margin-left:12px; min-width:48px; }

  /* Sección título */
  .seccion-titulo { font-weight:bold; font-size:10px; color:${accent}; border-bottom:1px solid ${accent}; padding-bottom:1px; margin:4px 0 2px; }

  /* Tabla de productos */
  .productos-table { width:100%; border-collapse:collapse; margin:2px 0; font-size:9px; }
  .productos-table th { background:${accent}; color:#fff; padding:3px 4px; text-align:center; font-weight:bold; border:1px solid ${accentDark}; font-size:9px; }
  .productos-table td { padding:2px 4px; border:1px solid #ccc; font-size:9px; vertical-align:top; }
  .productos-table td.cant { text-align:center; width:8%; white-space:nowrap; }
  .productos-table td.precio { text-align:right; width:13%; white-space:nowrap; }
  .productos-table td .extra { font-size:8px; color:#666; }
  .productos-table td.desc { color:#C0392B; }
  .productos-table tr.regalia td { background:#FFF8E1; }

  /* Vigencia (cotización) */
  .vigencia-box { font-size:9px; text-align:center; padding:3px 6px; border:1px solid #B8860B; background:#FFF7E0; margin:3px 0; line-height:1.3; }
  .vigencia-box b { color:#8B6914; }

  /* Bloque inferior: totales + info pago */
  .bottom-wrap { display:flex; gap:8px; margin-top:3px; align-items:flex-start; }
  .bottom-left { flex:1 1 0; min-width:0; font-size:9px; }
  .bottom-right { flex:0 0 46%; min-width:0; }

  .pago-box { padding:4px 6px; background:#E8F5E9; border:1px solid #2E7D32; border-radius:3px; font-size:9px; margin-bottom:3px; }
  .pago-box .pago-line { display:flex; justify-content:space-between; margin-bottom:1px; }
  .pago-box b { color:#1B5E20; }

  .notas-box { padding:3px 6px; background:#FFF7E0; border:1px solid #D4A017; font-size:9px; line-height:1.3; }
  .notas-box b { color:#8B6914; }

  /* Tabla totales */
  .totales-table { width:100%; border-collapse:collapse; font-size:9px; }
  .totales-table td { padding:3px 6px; border:1px solid #ccc; }
  .totales-table .lbl { background:#F4F6F8; font-weight:bold; width:58%; text-align:right; }
  .totales-table .val { text-align:right; white-space:nowrap; }
  .totales-table .desc-row .val { color:#C0392B; }
  .totales-table .total-row td { background:${accent}; color:#fff; font-weight:bold; font-size:11px; }
  .totales-table .total-row .lbl { background:${accent}; color:#fff; }

  /* Firma */
  .firma-section { display:flex; justify-content:space-between; margin-top:auto; padding-top:10px; gap:10px; }
  .firma-box { text-align:center; width:34%; }
  .firma-line { border-bottom:1px solid #000; height:14px; margin-bottom:1px; }
  .firma-label { font-size:9px; font-weight:bold; }

  /* Footer copia */
  .copy-label { text-align:center; font-size:9px; font-weight:bold; color:${accent}; text-transform:uppercase; padding-top:3px; border-top:1px dashed ${accent}; margin-top:5px; letter-spacing:2px; }

  /* Botones (solo pantalla) */
  .print-buttons { position:fixed; top:8px; right:8px; display:flex; gap:6px; z-index:1000; }
  .print-btn { background:${accent}; color:#fff; border:none; padding:8px 14px; border-radius:4px; cursor:pointer; font-weight:bold; font-size:12px; }

  @media print {
    @page { size: letter portrait; margin: 0; }
    .print-buttons { display:none !important; }
    * { -webkit-print-color-adjust: exact !important; color-adjust: exact !important; print-color-adjust: exact !important; }
    body { padding: 0.4in 0.4in !important; }
    .copy-section { page-break-inside:avoid; }
    .productos-table th, .totales-table .total-row td, .totales-table .total-row .lbl { background:${accent} !important; color:#fff !important; }
    .doc-header { background:linear-gradient(to bottom, #EAF2FE, #fff) !important; }
    .cbx.checked { background:${accent} !important; color:#fff !important; }
  }
`;

/* =========================================================================
 *  Helpers de bloques HTML
 * ========================================================================= */
const headerHTML = ({ config, titulo, numero, fecha, hora, tipoVenta }) => {
  const logoHTML = config.logo_url
    ? `<img src="${config.logo_url}" alt="Logo" class="logo-img" />`
    : '';
  const detalleNeg = [];
  if (config.rtn) detalleNeg.push(`RTN: ${esc(config.rtn)}`);
  if (config.direccion) detalleNeg.push(esc(config.direccion));
  const linTelMail = [];
  if (config.telefono) linTelMail.push(`Tel: ${esc(config.telefono)}`);
  if (config.email) linTelMail.push(esc(config.email));
  if (linTelMail.length) detalleNeg.push(linTelMail.join(' &middot; '));

  const tipoVentaHTML = tipoVenta
    ? `<div class="tipo-venta-box">
         <label><span class="cbx ${tipoVenta === 'credito' ? 'checked' : ''}">${tipoVenta === 'credito' ? 'X' : ''}</span>Crédito</label>
         <label><span class="cbx ${tipoVenta === 'contado' ? 'checked' : ''}">${tipoVenta === 'contado' ? 'X' : ''}</span>Contado</label>
       </div>`
    : '';

  return `
    <div class="doc-header">
      <div class="header-left">
        ${logoHTML}
        <div class="company-info">
          <div class="company-name">${esc(config.nombre_negocio || 'MI NEGOCIO')}</div>
          ${detalleNeg.map(d => `<div class="company-detail">${d}</div>`).join('')}
        </div>
      </div>
      <div class="header-center">
        <div class="doc-title">${titulo}</div>
      </div>
      <div class="header-right">
        ${numero ? `<div class="doc-numero">${esc(numero)}</div>` : ''}
        <div class="doc-fecha"><b>Fecha:</b> ${fecha}${hora ? ` &middot; ${hora}` : ''}</div>
        ${tipoVentaHTML}
      </div>
    </div>`;
};

const clienteHTML = ({ nombre, telefono, direccion, documento, vendedor }) => {
  const filaPrincipal = `
    <div class="cliente-row">
      <span class="lbl">Cliente:</span><span class="val">${esc(nombre || 'Consumidor Final')}</span>
      <span class="lbl mid">${documento ? 'RTN/DNI:' : 'Tel:'}</span><span class="val">${esc(documento || telefono || 'N/A')}</span>
    </div>`;
  const filaSecundaria = `
    <div class="cliente-row">
      <span class="lbl">Dirección:</span><span class="val">${esc(direccion || 'N/A')}</span>
      <span class="lbl mid">Vendedor:</span><span class="val">${esc(vendedor || 'N/A')}</span>
    </div>`;
  return filaPrincipal + filaSecundaria;
};

const itemsTableHTML = (items = [], { showDescColumn = true } = {}) => {
  const filas = items.map(item => {
    const nombre = esc(item.producto_nombre || item.producto?.nombre || 'Producto');
    const { cantidadDisplay, precioDisplay, equivBase, precioBase, tienePresentacion, unidad } = resolveItemPresentacion(item);
    const subtotal = parseFloat(item.subtotal || (cantidadDisplay * precioDisplay));
    const descMonto = parseFloat(item.descuento_monto || 0);
    const descTipo = item.descuento_tipo;
    const descValor = parseFloat(item.descuento_valor || 0);

    const cantidadFmt = Number.isInteger(cantidadDisplay) ? cantidadDisplay : cantidadDisplay.toFixed(2);
    const unidadFmt = esc(unidad);

    const extras = [];
    const codigo = item.producto_codigo || item.producto?.codigo_barras;
    if (codigo) extras.push(`Cód: ${esc(codigo)}`);
    if (item.marca) extras.push(esc(item.marca));
    if (tienePresentacion) {
      const totalUnidadesFmt = Number.isInteger(equivBase) ? equivBase : equivBase.toFixed(2);
      extras.push(`= ${totalUnidadesFmt} unidades (${formatCurrency(precioBase)} c/u)`);
    }
    if (item.lote_numero) extras.push(`Lote: ${esc(item.lote_numero)}`);
    const vence = item.lote_fecha_vencimiento || item.fecha_vencimiento;
    if (vence) extras.push(`Vence: ${esc(formatFechaCorta(vence))}`);

    const descCelda = descMonto > 0
      ? `L. ${descMonto.toFixed(2)}${descTipo === 'porcentaje' ? ` (${descValor}%)` : ''}`
      : '—';

    return `
      <tr>
        <td class="cant">${cantidadFmt} ${unidadFmt}</td>
        <td>
          <strong>${nombre}</strong>
          ${extras.length ? `<div class="extra">${extras.join(' &middot; ')}</div>` : ''}
        </td>
        <td class="precio">${formatCurrency(precioDisplay)}</td>
        ${showDescColumn ? `<td class="precio desc">${descCelda}</td>` : ''}
        <td class="precio"><b>${formatCurrency(subtotal)}</b></td>
      </tr>`;
  }).join('');

  return `
    <table class="productos-table">
      <thead>
        <tr>
          <th style="width:10%">Cant.</th>
          <th>Descripción</th>
          <th style="width:13%">P. Unit.</th>
          ${showDescColumn ? '<th style="width:13%">Desc/Reb.</th>' : ''}
          <th style="width:14%">Total</th>
        </tr>
      </thead>
      <tbody>${filas}</tbody>
    </table>`;
};

const totalesTableHTML = (data) => {
  const subtotalVenta = parseFloat(data.subtotal);
  const totalVenta = parseFloat(data.total || 0);
  const descGlobal = parseFloat(data.descuento_monto || 0);
  const descTipoG = data.descuento_tipo;
  const descValorG = parseFloat(data.descuento_valor || 0);
  const subOrTotal = Number.isFinite(subtotalVenta) && subtotalVenta > 0 ? subtotalVenta : totalVenta;

  let filas = `<tr><td class="lbl">Subtotal:</td><td class="val">${formatCurrency(subOrTotal)}</td></tr>`;
  if (descGlobal > 0) {
    filas += `<tr class="desc-row"><td class="lbl">Descuento${descTipoG === 'porcentaje' ? ` (${descValorG}%)` : ''}:</td><td class="val">− ${formatCurrency(descGlobal)}</td></tr>`;
  }
  filas += `<tr class="total-row"><td class="lbl">TOTAL:</td><td class="val">${formatCurrency(totalVenta)}</td></tr>`;
  return `<table class="totales-table">${filas}</table>`;
};

const firmaHTML = (texto = 'Firma Autorizada') => `
  <div class="firma-section">
    <div class="firma-box">
      <div class="firma-line"></div>
      <div class="firma-label">Recibí conforme</div>
    </div>
    <div class="firma-box">
      <div class="firma-line"></div>
      <div class="firma-label">${esc(texto)}</div>
    </div>
  </div>`;

const printScript = () => `
  <script>
    if (new URLSearchParams(window.location.search).get('print') === '1') {
      window.addEventListener('load', function () { setTimeout(function () { window.print(); }, 300); });
    }
  </script>`;

/* =========================================================================
 *  Bloque de una copia (factura)
 * ========================================================================= */
const buildFacturaCopy = (data, config, copyLabel) => {
  const { fecha, hora } = formatFechaHora(data.fecha);
  const clienteNombre = data.cliente_nombre || data.cliente?.nombre || '';
  const clienteTel = data.cliente_telefono || data.cliente?.telefono || '';
  const clienteDir = data.cliente_direccion || data.cliente?.direccion || '';
  const clienteDoc = data.cliente_documento || data.cliente?.documento || data.cliente?.rtn || '';
  const vendedor = data.vendedor_nombre || data.vendedor || data.usuario?.nombre || '';
  const numero = data.numero || `#${data.id || data.venta_id || ''}`;
  const tipoVenta = data.metodo_pago === 'credito' ? 'credito' : 'contado';

  const items = data.items || data.detalles || [];

  const pagoBlocks = [];
  if (data.metodo_pago) pagoBlocks.push(`<div class="pago-line"><b>Método pago:</b><span>${esc((data.metodo_pago || '').toUpperCase())}</span></div>`);
  if (data.estado) pagoBlocks.push(`<div class="pago-line"><b>Estado:</b><span>${esc((data.estado || '').toUpperCase())}</span></div>`);

  return `
    <div class="copy-section">
      ${headerHTML({ config, titulo: 'FACTURA', numero, fecha, hora, tipoVenta })}
      ${clienteHTML({ nombre: clienteNombre, telefono: clienteTel, direccion: clienteDir, documento: clienteDoc, vendedor })}

      <div class="seccion-titulo">Detalle de Productos</div>
      ${itemsTableHTML(items, { showDescColumn: true })}

      <div class="bottom-wrap">
        <div class="bottom-left">
          ${pagoBlocks.length ? `<div class="pago-box">${pagoBlocks.join('')}</div>` : ''}
          ${data.notas ? `<div class="notas-box"><b>Notas:</b> ${esc(data.notas)}</div>` : ''}
        </div>
        <div class="bottom-right">
          ${totalesTableHTML(data)}
        </div>
      </div>

      ${firmaHTML('Firma Autorizada')}
      <div class="copy-label">${esc(copyLabel)}</div>
    </div>`;
};

/* =========================================================================
 *  Bloque de una copia (cotización)
 * ========================================================================= */
const buildCotizacionCopy = (data, config, copyLabel) => {
  const fechaIni = formatFechaCorta(data.fecha) || formatFechaHora(data.fecha).fecha;
  const fechaVen = data.fecha_vencimiento
    ? formatFechaCorta(data.fecha_vencimiento)
    : (data.dias_vigencia ? `${data.dias_vigencia} días desde la emisión` : '');

  const clienteNombre = data.cliente_nombre || data.cliente?.nombre || '';
  const clienteTel = data.cliente_telefono || data.cliente?.telefono || '';
  const clienteDir = data.cliente_direccion || data.cliente?.direccion || '';
  const clienteDoc = data.cliente_documento || data.cliente?.documento || data.cliente?.rtn || '';
  const vendedor = data.vendedor_nombre || data.vendedor || data.usuario?.nombre || '';
  const numero = data.numero || `#${data.id || ''}`;

  const items = data.items || data.detalles || [];

  return `
    <div class="copy-section">
      ${headerHTML({ config, titulo: 'COTIZACIÓN', numero, fecha: fechaIni })}
      ${clienteHTML({ nombre: clienteNombre, telefono: clienteTel, direccion: clienteDir, documento: clienteDoc, vendedor })}

      ${fechaVen ? `<div class="vigencia-box"><b>Vigencia:</b> ${esc(fechaVen)} &nbsp;|&nbsp; <b>Esta cotización no es un compromiso de venta; los precios pueden variar.</b></div>` : ''}

      <div class="seccion-titulo">Detalle de Productos</div>
      ${itemsTableHTML(items, { showDescColumn: true })}

      <div class="bottom-wrap">
        <div class="bottom-left">
          ${data.notas ? `<div class="notas-box"><b>Notas:</b> ${esc(data.notas)}</div>` : ''}
        </div>
        <div class="bottom-right">
          ${totalesTableHTML(data)}
        </div>
      </div>

      ${firmaHTML('Aceptación del Cliente')}
      <div class="copy-label">${esc(copyLabel)}</div>
    </div>`;
};

/* =========================================================================
 *  Documentos públicos
 * ========================================================================= */

/**
 * HTML de factura — 2 copias en una hoja carta (Original Cliente / Copia Emisor).
 * Se separan por línea de corte.
 */
export const generateFacturaHTML = (data, config = {}, options = {}) => {
  const copias = options.copias || ['Original: Cliente', 'Copia: Emisor'];
  const partes = copias.map((label, i) =>
    (i > 0 ? '<div class="cut-line"></div>' : '') + buildFacturaCopy(data, config, label)
  ).join('');

  return `<!DOCTYPE html><html><head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>Factura ${esc(data.numero || data.id || '')}</title>
<style>${docStyles()}</style>
</head><body>
<div class="page-container">${partes}</div>
${printScript()}
</body></html>`;
};

/**
 * HTML de cotización — 2 copias en una hoja carta (Cliente / Archivo).
 */
export const generateCotizacionHTML = (data, config = {}, options = {}) => {
  const copias = options.copias || ['Original: Cliente', 'Copia: Archivo'];
  const partes = copias.map((label, i) =>
    (i > 0 ? '<div class="cut-line"></div>' : '') + buildCotizacionCopy(data, config, label)
  ).join('');

  return `<!DOCTYPE html><html><head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>Cotización ${esc(data.numero || data.id || '')}</title>
<style>${docStyles()}</style>
</head><body>
<div class="page-container">${partes}</div>
${printScript()}
</body></html>`;
};

/* =========================================================================
 *  Ticket térmico 80mm (sin cambios funcionales)
 * ========================================================================= */
export const generateTicketHTML = (data, config = {}, options = {}) => {
  const { fecha, hora } = formatFechaHora(data.fecha);
  const showInternalId = options.showInternalId !== false;

  const logoHTML = config.logo_url
    ? `<img src="${config.logo_url}" alt="Logo" style="max-height:40px;max-width:150px;margin-bottom:6px" />`
    : '';

  const itemsHTML = (data.items || data.detalles || []).map(item => {
    const nombre = item.producto_nombre || item.producto?.nombre || 'Producto';
    const { cantidadDisplay, precioDisplay, equivBase, precioBase, tienePresentacion, unidad } = resolveItemPresentacion(item);
    const subtotal = parseFloat(item.subtotal || (cantidadDisplay * precioDisplay));

    const cantidadFmt = Number.isInteger(cantidadDisplay) ? cantidadDisplay : cantidadDisplay.toFixed(2);
    const presentacionHTML = tienePresentacion
      ? `<div class="item-lote">= ${Number.isInteger(equivBase) ? equivBase : equivBase.toFixed(2)} unidades (${formatCurrency(precioBase)} c/u)</div>`
      : '';

    const venceItem = item.lote_fecha_vencimiento || item.fecha_vencimiento;
    const loteHTML = item.lote_numero
      ? `<div class="item-lote">Lote: ${item.lote_numero}${venceItem ? ` &middot; Vence: ${formatFechaCorta(venceItem)}` : ''}</div>`
      : (venceItem ? `<div class="item-lote">Vence: ${formatFechaCorta(venceItem)}</div>` : '');
    const descMonto = parseFloat(item.descuento_monto || 0);
    const descTipo = item.descuento_tipo;
    const descValor = parseFloat(item.descuento_valor || 0);
    const descHTML = descMonto > 0
      ? `<div class="item-line" style="color:#cc0000"><span>Desc${descTipo === 'porcentaje' ? ` ${descValor}%` : ''}</span><span>−${formatCurrency(descMonto)}</span></div>`
      : '';
    return `
      <div class="item">
        <div class="item-name">${nombre}</div>
        ${presentacionHTML}
        ${loteHTML}
        <div class="item-line">
          <span>${cantidadFmt} ${unidad} x ${formatCurrency(precioDisplay)}</span>
          <span class="item-total">${formatCurrency(subtotal)}</span>
        </div>
        ${descHTML}
      </div>`;
  }).join('');

  const clienteNombre = data.cliente_nombre || data.cliente?.nombre || '';
  const vendedor = data.vendedor_nombre || data.vendedor || data.usuario?.nombre || '';

  return `<!DOCTYPE html><html><head><meta charset="utf-8">
<style>
  @page { size:80mm auto; margin:0; }
  * { margin:0; padding:0; box-sizing:border-box; }
  body { font-family:'Courier New',monospace; margin:0; padding:4px 4px 8px; font-size:11px; width:80mm; line-height:1.25; }
  .header { text-align:center; border-bottom:1px dashed #000; padding-bottom:4px; margin-bottom:4px; }
  .logo { margin-bottom:2px; }
  .biz-name { font-size:14px; font-weight:bold; margin-bottom:1px; }
  .biz-detail { font-size:9px; color:#444; line-height:1.3; }
  .separator { border-top:1px dashed #000; margin:4px 0; }
  .info-line { display:flex; justify-content:space-between; font-size:10px; margin-bottom:1px; }
  .info-line b { min-width:54px; }
  .items { padding:2px 0; }
  .item { margin-bottom:3px; }
  .item-name { font-weight:bold; font-size:11px; line-height:1.2; }
  .item-lote { font-size:9px; color:#555; }
  .item-line { display:flex; justify-content:space-between; font-size:10px; }
  .item-total { font-weight:bold; }
  .total-section { border-top:2px solid #000; padding-top:4px; margin-top:2px; }
  .total-line { display:flex; justify-content:space-between; font-size:14px; font-weight:bold; margin-bottom:2px; }
  .pago-line { display:flex; justify-content:space-between; font-size:10px; margin-bottom:1px; }
  .footer { text-align:center; border-top:1px dashed #000; margin-top:6px; padding-top:4px; font-size:9px; color:#555; }
  .footer p { margin:1px 0; }
</style></head><body>
<div class="header">
  <div class="logo">${logoHTML}</div>
  <div class="biz-name">${config.nombre_negocio || 'MI NEGOCIO'}</div>
  <div class="biz-detail">
    ${config.rtn ? `RTN: ${config.rtn}<br>` : ''}
    ${config.direccion || ''}
    ${config.telefono ? `<br>Tel: ${config.telefono}` : ''}
  </div>
</div>

<div class="info-line"><b>Fecha:</b><span>${fecha} ${hora}</span></div>
${showInternalId ? `<div class="info-line"><b>Ref:</b><span>#${data.id || data.venta_id || ''}</span></div>` : ''}
<div class="info-line"><b>Cliente:</b><span>${clienteNombre || 'Consumidor Final'}</span></div>
<div class="info-line"><b>Vendedor:</b><span>${vendedor || 'N/A'}</span></div>

<div class="separator"></div>
<div class="items">${itemsHTML}</div>

<div class="total-section">
  ${(() => {
    const subtotalVenta = parseFloat(data.subtotal);
    const totalVenta = parseFloat(data.total || 0);
    const descGlobal = parseFloat(data.descuento_monto || 0);
    const descTipoG = data.descuento_tipo;
    const descValorG = parseFloat(data.descuento_valor || 0);
    if (descGlobal > 0 && Number.isFinite(subtotalVenta) && subtotalVenta > 0) {
      return `
        <div class="pago-line"><span>Subtotal:</span><span>${formatCurrency(subtotalVenta)}</span></div>
        <div class="pago-line" style="color:#cc0000"><span>Desc${descTipoG === 'porcentaje' ? ` ${descValorG}%` : ''}:</span><span>−${formatCurrency(descGlobal)}</span></div>`;
    }
    return '';
  })()}
  <div class="total-line"><span>TOTAL:</span><span>${formatCurrency(data.total)}</span></div>
  ${data.metodo_pago ? `<div class="pago-line"><b>Pago:</b><span>${(data.metodo_pago || '').toUpperCase()}</span></div>` : ''}
  ${data.estado ? `<div class="pago-line"><b>Estado:</b><span>${(data.estado || '').toUpperCase()}</span></div>` : ''}
</div>

${data.notas ? `<div style="margin-top:6px;font-size:9px"><b>Notas:</b> ${data.notas}</div>` : ''}

<div class="footer">
  <p>Gracias por su compra!</p>
  ${config.nombre_negocio ? `<p>${config.nombre_negocio}</p>` : ''}
</div>
</body></html>`;
};

/* =========================================================================
 *  Orden de bodega — compacta, carta portrait
 * ========================================================================= */
export const generateOrdenBodegaHTML = (data, config = {}) => {
  const { fecha, hora } = formatFechaHora(data.fecha);

  const items = data.items || data.detalles || [];
  const totalItems = items.length;
  const totalUnidades = items.reduce((sum, it) => sum + parseFloat(it.cantidad || it.cantidad_solicitada || 0), 0);

  const itemsHTML = items.map((item, i) => {
    const nombre = esc(item.producto_nombre || item.producto?.nombre || 'Producto');
    const cantidad = parseFloat(item.cantidad || item.cantidad_solicitada || 0);
    const unidad = esc(item.unidad_nombre || 'ud');
    const marca = item.marca ? esc(item.marca) : '';
    const codigo = item.producto_codigo ? esc(item.producto_codigo) : '';
    const extras = [marca && `<span class="extra-marca">${marca}</span>`, codigo && `<span class="extra-codigo">${codigo}</span>`].filter(Boolean).join(' &middot; ');
    return `
      <tr>
        <td class="num">${i + 1}</td>
        <td><strong>${nombre}</strong>${extras ? `<div class="extras">${extras}</div>` : ''}</td>
        <td class="cant-grande">${Number.isInteger(cantidad) ? cantidad : cantidad.toFixed(2)}</td>
        <td class="unidad">${unidad}</td>
        <td class="check"></td>
      </tr>`;
  }).join('');

  const clienteNombre = esc(data.cliente_nombre || data.cliente?.nombre || '');
  const clienteDir = esc(data.cliente_direccion || data.cliente?.direccion || '');
  const clienteTel = esc(data.cliente_telefono || data.cliente?.telefono || '');

  return `<!DOCTYPE html><html><head><meta charset="utf-8">
<title>Orden de Bodega #${esc(data.id || '')}</title>
<style>
  @page { size: letter portrait; margin: 0; }
  * { margin:0; padding:0; box-sizing:border-box; }
  html, body { width:100%; }
  body { font-family: Arial, Helvetica, sans-serif; font-size:11px; color:#000; line-height:1.25; padding: 0.5in 0.45in; }
  .header { border-bottom:2px solid #333; padding-bottom:6px; margin-bottom:8px; display:flex; justify-content:space-between; align-items:flex-start; gap:10px; }
  .header-left .titulo { font-size:18px; font-weight:bold; letter-spacing:1px; }
  .header-left .neg { font-size:11px; color:#555; margin-top:2px; }
  .header-right { text-align:right; font-size:11px; }
  .header-right .pedido { font-size:14px; font-weight:bold; color:#CC0000; }
  .info-grid { display:flex; gap:14px; margin-bottom:8px; font-size:11px; padding:6px 8px; background:#f8f9fa; border:1px solid #e0e3e7; border-radius:4px; }
  .info-item { flex:1; }
  .info-item b { color:#333; }
  table { width:100%; border-collapse:collapse; }
  th { background:#333; color:#fff; padding:6px 4px; font-size:10px; text-transform:uppercase; letter-spacing:0.5px; border:1px solid #222; }
  td { padding:5px 6px; border:1px solid #ccc; font-size:11px; vertical-align:middle; }
  td.num { text-align:center; width:5%; font-weight:bold; }
  td.cant-grande { text-align:center; width:10%; font-size:16px; font-weight:bold; }
  td.unidad { text-align:center; width:9%; color:#555; }
  td.check { width:9%; border:1.5px solid #555; }
  td .extras { font-size:9px; color:#666; margin-top:1px; }
  .extra-marca { color:#17A2B8; }
  .extra-codigo { color:#888; }
  .resumen { margin-top:10px; padding:6px 10px; background:#FFF7E0; border:1px solid #D4A017; font-size:11px; display:flex; gap:20px; }
  .resumen b { color:#8B6914; }
  .firma-row { display:flex; justify-content:space-between; margin-top:30px; gap:20px; }
  .firma-box { text-align:center; flex:1; max-width:40%; }
  .firma-line { border-top:1px solid #333; padding-top:4px; font-size:10px; font-weight:bold; }
  .print-buttons { position:fixed; top:8px; right:8px; }
  .print-btn { background:#333; color:#fff; border:none; padding:8px 14px; border-radius:4px; cursor:pointer; font-weight:bold; }
  @media print {
    @page { size: letter portrait; margin: 0; }
    .print-buttons { display:none !important; }
    * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
    body { padding: 0.5in 0.45in !important; }
    th { background:#333 !important; color:#fff !important; }
  }
</style></head><body>
<div class="print-buttons"><button class="print-btn" onclick="window.print()">Imprimir</button></div>

<div class="header">
  <div class="header-left">
    <div class="titulo">ORDEN DE PREPARACIÓN — BODEGA</div>
    <div class="neg">${esc(config.nombre_negocio || '')}</div>
  </div>
  <div class="header-right">
    <div class="pedido">Pedido #${esc(data.id || '')}</div>
    <div><b>Fecha:</b> ${fecha} ${hora}</div>
  </div>
</div>

<div class="info-grid">
  <div class="info-item"><b>Cliente:</b> ${clienteNombre || 'N/A'}${clienteTel ? ` &middot; Tel: ${clienteTel}` : ''}</div>
  ${clienteDir ? `<div class="info-item"><b>Dirección:</b> ${clienteDir}</div>` : ''}
</div>

<table>
  <thead><tr>
    <th>#</th><th style="text-align:left">Producto</th><th>Cantidad</th><th>Unidad</th><th>✓</th>
  </tr></thead>
  <tbody>${itemsHTML}</tbody>
</table>

<div class="resumen">
  <div><b>Total ítems:</b> ${totalItems}</div>
  <div><b>Total unidades:</b> ${Number.isInteger(totalUnidades) ? totalUnidades : totalUnidades.toFixed(2)}</div>
  ${data.total ? `<div><b>Total pedido:</b> ${formatCurrency(data.total)}</div>` : ''}
</div>

<div class="firma-row">
  <div class="firma-box"><div class="firma-line">Preparado por (Bodega)</div></div>
  <div class="firma-box"><div class="firma-line">Recibido por (Repartidor)</div></div>
</div>

${printScript()}
</body></html>`;
};

/* =========================================================================
 *  Voucher de pago a empleado (sin cambios)
 * ========================================================================= */
export const generateVoucherHTML = (data, config = {}) => {
  const { fecha: fechaEmision, hora: horaEmision } = formatFechaHora(data.created_at);
  const periodoInicio = data.periodo_inicio ? new Date(data.periodo_inicio).toLocaleDateString('es-HN', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
  const periodoFin = data.periodo_fin ? new Date(data.periodo_fin).toLocaleDateString('es-HN', { day: '2-digit', month: 'short', year: 'numeric' }) : '';

  const logoHTML = config.logo_url
    ? `<img src="${config.logo_url}" alt="Logo" style="max-height:50px;max-width:180px;margin-bottom:6px" />`
    : '';

  let ingresosHTML = '';
  if (parseFloat(data.salario_base) > 0) {
    ingresosHTML += `<tr><td class="concepto">Salario base</td><td class="monto ingreso">${formatCurrency(data.salario_base)}</td></tr>`;
  }
  if (parseFloat(data.total_comisiones) > 0) {
    ingresosHTML += `<tr><td class="concepto">Comisiones por ventas</td><td class="monto ingreso">${formatCurrency(data.total_comisiones)}</td></tr>`;
  }
  if (parseFloat(data.total_bonos) > 0) {
    ingresosHTML += `<tr><td class="concepto">Bonos</td><td class="monto ingreso">${formatCurrency(data.total_bonos)}</td></tr>`;
  }
  if (parseFloat(data.otros_ingresos) > 0) {
    ingresosHTML += `<tr><td class="concepto">Otros ingresos</td><td class="monto ingreso">${formatCurrency(data.otros_ingresos)}</td></tr>`;
  }

  const detallesIngresos = (data.detalles || []).filter(d => d.tipo === 'ingreso');
  for (const det of detallesIngresos) {
    ingresosHTML += `<tr class="detalle-row"><td class="concepto-detalle">&nbsp;&nbsp;&bull; ${det.concepto}</td><td class="monto-detalle">${formatCurrency(det.monto)}</td></tr>`;
  }

  let deduccionesHTML = '';
  if (parseFloat(data.total_penalizaciones) > 0) {
    deduccionesHTML += `<tr><td class="concepto">Penalizaciones</td><td class="monto deduccion">- ${formatCurrency(data.total_penalizaciones)}</td></tr>`;
  }
  if (parseFloat(data.otras_deducciones) > 0) {
    deduccionesHTML += `<tr><td class="concepto">Otras deducciones</td><td class="monto deduccion">- ${formatCurrency(data.otras_deducciones)}</td></tr>`;
  }

  const detallesDeducciones = (data.detalles || []).filter(d => d.tipo === 'deduccion');
  for (const det of detallesDeducciones) {
    deduccionesHTML += `<tr class="detalle-row"><td class="concepto-detalle">&nbsp;&nbsp;&bull; ${det.concepto}</td><td class="monto-detalle deduccion">- ${formatCurrency(det.monto)}</td></tr>`;
  }

  const firmaEmisorHTML = data.firma_emisor
    ? `<img src="${data.firma_emisor}" style="max-height:60px;max-width:180px" />`
    : '<div class="sin-firma">Sin firma</div>';

  const firmaEmpleadoHTML = data.firma_empleado
    ? `<img src="${data.firma_empleado}" style="max-height:60px;max-width:180px" />`
    : '<div class="sin-firma">Pendiente</div>';

  const estadoLabel = {
    borrador: 'BORRADOR',
    emitido: 'EMITIDO',
    firmado: 'FIRMADO',
    pagado: 'PAGADO',
    anulado: 'ANULADO',
  };

  const estadoColor = {
    borrador: '#6c757d',
    emitido: '#007bff',
    firmado: '#17a2b8',
    pagado: '#28a745',
    anulado: '#dc3545',
  };

  return `<!DOCTYPE html><html><head><meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<style>
  * { margin:0; padding:0; box-sizing:border-box; }
  body { font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; color:#333; font-size:12px; padding:20px; }
  .voucher { max-width:800px; margin:0 auto; border:2px solid #007BFF; border-radius:8px; overflow:hidden; }
  .voucher-header { background:linear-gradient(135deg, #007BFF, #0056b3); padding:20px; color:#fff; display:flex; justify-content:space-between; align-items:flex-start; }
  .negocio-info { flex:1; }
  .negocio-nombre { font-size:20px; font-weight:bold; margin-bottom:4px; }
  .negocio-detalle { font-size:10px; opacity:0.9; line-height:1.5; }
  .voucher-meta { text-align:right; }
  .voucher-titulo { font-size:18px; font-weight:bold; letter-spacing:2px; margin-bottom:6px; background:rgba(255,255,255,0.2); padding:4px 12px; border-radius:4px; }
  .voucher-dato { font-size:11px; line-height:1.8; opacity:0.95; }
  .estado-badge { display:inline-block; padding:3px 12px; border-radius:12px; font-size:10px; font-weight:bold; letter-spacing:1px; background:rgba(255,255,255,0.3); margin-top:6px; }

  .voucher-body { padding:20px; }

  .empleado-section { background:#f8f9fa; border-radius:6px; padding:15px; margin-bottom:18px; display:flex; justify-content:space-between; }
  .empleado-info { }
  .empleado-nombre { font-size:16px; font-weight:bold; color:#333; margin-bottom:4px; }
  .empleado-detail { font-size:11px; color:#666; line-height:1.6; }
  .periodo-info { text-align:right; }
  .periodo-label { font-size:10px; text-transform:uppercase; color:#888; letter-spacing:1px; }
  .periodo-value { font-size:13px; font-weight:600; color:#333; margin-top:2px; }

  .desglose-section { margin-bottom:15px; }
  .desglose-title { font-size:12px; font-weight:700; text-transform:uppercase; letter-spacing:1px; padding:8px 0; border-bottom:2px solid #007BFF; margin-bottom:8px; }
  .desglose-title.deduccion { border-bottom-color:#dc3545; }

  table { width:100%; border-collapse:collapse; }
  .concepto { padding:6px 0; font-size:13px; }
  .concepto-detalle { padding:2px 0; font-size:11px; color:#888; }
  .monto { padding:6px 0; text-align:right; font-size:13px; font-weight:600; }
  .monto-detalle { padding:2px 0; text-align:right; font-size:11px; color:#888; }
  .ingreso { color:#28a745; }
  .deduccion { color:#dc3545; }
  .detalle-row td { border:none; }

  .subtotal-row { border-top:1px solid #ddd; }
  .subtotal-row td { padding-top:10px; font-weight:700; font-size:14px; }

  .neto-section { background:#007BFF; color:#fff; border-radius:8px; padding:15px 20px; margin:15px 0; display:flex; justify-content:space-between; align-items:center; }
  .neto-label { font-size:14px; font-weight:600; letter-spacing:1px; }
  .neto-value { font-size:24px; font-weight:700; }

  .pago-section { display:flex; justify-content:space-between; margin:15px 0; padding:12px; background:#E8F5E9; border-radius:6px; }
  .pago-item { }
  .pago-label { font-size:10px; color:#666; text-transform:uppercase; }
  .pago-value { font-size:13px; font-weight:600; color:#333; margin-top:2px; text-transform:capitalize; }

  .firmas-section { display:flex; justify-content:space-between; margin-top:30px; padding-top:20px; }
  .firma-box { width:45%; text-align:center; }
  .firma-img { min-height:60px; margin-bottom:8px; display:flex; align-items:center; justify-content:center; }
  .firma-line { border-top:1px solid #333; padding-top:6px; }
  .firma-nombre { font-size:11px; font-weight:600; color:#333; }
  .firma-cargo { font-size:10px; color:#888; }
  .sin-firma { color:#ccc; font-size:11px; font-style:italic; min-height:60px; display:flex; align-items:center; justify-content:center; }

  .notas-section { margin-top:15px; padding:10px; background:#FFF3CD; border-radius:4px; font-size:11px; }

  .footer { text-align:center; padding:12px; border-top:1px dashed #ccc; margin-top:15px; font-size:10px; color:#888; }
  .footer .copy-note { font-style:italic; margin-top:4px; }

  .anulado-watermark { position:relative; }
  .anulado-watermark::after { content:'ANULADO'; position:absolute; top:50%; left:50%; transform:translate(-50%,-50%) rotate(-30deg); font-size:80px; font-weight:bold; color:rgba(220,53,69,0.15); pointer-events:none; z-index:10; }

  @media print {
    body { padding:0; }
    .voucher { border:1px solid #333; }
  }
</style></head><body>
<div class="voucher ${data.estado === 'anulado' ? 'anulado-watermark' : ''}">
  <div class="voucher-header">
    <div class="negocio-info">
      ${logoHTML}
      <div class="negocio-nombre">${config.nombre_negocio || 'MI NEGOCIO'}</div>
      <div class="negocio-detalle">
        ${config.rtn ? `RTN: ${config.rtn}<br>` : ''}
        ${config.direccion ? `${config.direccion}<br>` : ''}
        ${config.telefono ? `Tel: ${config.telefono}` : ''}
      </div>
    </div>
    <div class="voucher-meta">
      <div class="voucher-titulo">VOUCHER DE PAGO</div>
      <div class="voucher-dato">
        <strong>No:</strong> ${data.numero_voucher}<br>
        <strong>Fecha:</strong> ${fechaEmision}<br>
        <strong>Hora:</strong> ${horaEmision}
      </div>
      <div class="estado-badge" style="color:${estadoColor[data.estado] || '#333'}">${estadoLabel[data.estado] || data.estado}</div>
    </div>
  </div>

  <div class="voucher-body">
    <div class="empleado-section">
      <div class="empleado-info">
        <div class="empleado-nombre">${data.empleado_nombre || 'Empleado'}</div>
        <div class="empleado-detail">
          Cargo: ${data.empleado_rol || 'N/A'}<br>
          ${data.empleado_email ? `Email: ${data.empleado_email}` : ''}
        </div>
      </div>
      <div class="periodo-info">
        <div class="periodo-label">Período</div>
        <div class="periodo-value">${periodoInicio}<br>al ${periodoFin}</div>
      </div>
    </div>

    <div class="desglose-section">
      <div class="desglose-title">Ingresos</div>
      <table>${ingresosHTML}
        <tr class="subtotal-row">
          <td class="concepto">Total Ingresos</td>
          <td class="monto ingreso">${formatCurrency(data.total_ingresos)}</td>
        </tr>
      </table>
    </div>

    ${parseFloat(data.total_deducciones) > 0 ? `
    <div class="desglose-section">
      <div class="desglose-title deduccion">Deducciones</div>
      <table>${deduccionesHTML}
        <tr class="subtotal-row">
          <td class="concepto">Total Deducciones</td>
          <td class="monto deduccion">- ${formatCurrency(data.total_deducciones)}</td>
        </tr>
      </table>
    </div>` : ''}

    <div class="neto-section">
      <span class="neto-label">MONTO NETO A PAGAR</span>
      <span class="neto-value">${formatCurrency(data.monto_neto)}</span>
    </div>

    <div class="pago-section">
      <div class="pago-item">
        <div class="pago-label">Método de Pago</div>
        <div class="pago-value">${data.metodo_pago || 'Efectivo'}</div>
      </div>
      ${data.referencia_pago ? `
      <div class="pago-item">
        <div class="pago-label">Referencia</div>
        <div class="pago-value">${data.referencia_pago}</div>
      </div>` : ''}
      <div class="pago-item">
        <div class="pago-label">Emitido por</div>
        <div class="pago-value">${data.emitido_por_nombre || 'Administrador'}</div>
      </div>
    </div>

    ${data.notas ? `<div class="notas-section"><strong>Notas:</strong> ${data.notas}</div>` : ''}

    <div class="firmas-section">
      <div class="firma-box">
        <div class="firma-img">${firmaEmisorHTML}</div>
        <div class="firma-line">
          <div class="firma-nombre">${data.emitido_por_nombre || 'Administrador'}</div>
          <div class="firma-cargo">Emisor / Autorizado</div>
        </div>
      </div>
      <div class="firma-box">
        <div class="firma-img">${firmaEmpleadoHTML}</div>
        <div class="firma-line">
          <div class="firma-nombre">${data.empleado_nombre || 'Empleado'}</div>
          <div class="firma-cargo">Recibido conforme</div>
        </div>
      </div>
    </div>
  </div>

  <div class="footer">
    <p>Este documento es un comprobante de pago oficial de ${config.nombre_negocio || 'la empresa'}</p>
    <p class="copy-note">Copia digital - ${data.numero_voucher}</p>
  </div>
</div>
</body></html>`;
};

export default {
  generateFacturaHTML,
  generateCotizacionHTML,
  generateTicketHTML,
  generateOrdenBodegaHTML,
  generateVoucherHTML,
  formatCurrency,
  resolveItemPresentacion,
};
